import fs from 'fs';
const PREFIX = '[SuperatomSDK]';
const LOG_FILE_PATH = 'superatom-sdk.log';

/**
 * Log levels in hierarchical order
 * - errors: only error logs
 * - warnings: warning + error logs
 * - info: info + warning + error logs
 * - verbose: all logs including debug
 */
export type LogLevel = 'errors' | 'warnings' | 'info' | 'verbose';

// open a file in stream write mode. if no file is specified then create it.
let LOGSTREAM = fs.createWriteStream(LOG_FILE_PATH, { flags: 'a' });

/**
 * Internal log level hierarchy mapping
 */
const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  errors: 0,
  warnings: 1,
  info: 2,
  verbose: 3,
};

const MESSAGE_LEVEL_PRIORITY: Record<'error' | 'warn' | 'info' | 'debug', number> = {
  error: 0,
  warn: 1,
  info: 2,
  debug: 3,
};

/**
 * Logger class with environment-based log level support
 */
class Logger {
  private currentLevel: LogLevel;
  private currentLevelPriority: number;

  constructor() {
    // Read log level from environment variable, default to 'info'
    const envLevel = (process.env.SUPERATOM_LOG_LEVEL || 'info').toLowerCase();

    // Validate and set log level
    if (this.isValidLogLevel(envLevel)) {
      this.currentLevel = envLevel as LogLevel;
    } else {
      this.currentLevel = 'info';
      console.warn(
        `${PREFIX} Invalid log level "${envLevel}". Using default "info". Valid levels: errors, warnings, info, verbose`
      );
    }

    this.currentLevelPriority = LOG_LEVEL_PRIORITY[this.currentLevel];
  }

  /**
   * Check if a string is a valid log level
   */
  private isValidLogLevel(level: string): level is LogLevel {
    return level === 'errors' || level === 'warnings' || level === 'info' || level === 'verbose';
  }

  /**
   * Check if a message should be logged based on current log level
   */
  private shouldLog(messageLevel: 'error' | 'warn' | 'info' | 'debug'): boolean {
    const messagePriority = MESSAGE_LEVEL_PRIORITY[messageLevel];
    return messagePriority <= this.currentLevelPriority;
  }

  /**
   * Get current log level
   */
  getLogLevel(): LogLevel {
    return this.currentLevel;
  }

  /**
   * Set log level programmatically
   */
  setLogLevel(level: LogLevel): void {
    this.currentLevel = level;
    this.currentLevelPriority = LOG_LEVEL_PRIORITY[level];
  }

  /**
   * Log info message (shown for info and verbose levels)
   */
  info(...args: any[]): void {
    if (this.shouldLog('info')) {
      console.log(PREFIX, ...args);
    }
  }

  /**
   * Log error message (shown for all levels)
   */
  error(...args: any[]): void {
    if (this.shouldLog('error')) {
      console.error(PREFIX, ...args);
    }
  }

  /**
   * Log warning message (shown for warnings, info, and verbose levels)
   */
  warn(...args: any[]): void {
    if (this.shouldLog('warn')) {
      console.warn(PREFIX, ...args);
    }
  }

  /**
   * Log debug message (only shown for verbose level)
   */
  debug(...args: any[]): void {
    if (this.shouldLog('debug')) {
      console.log(PREFIX, '[DEBUG]', ...args);
    }
  }

  /**
   * Write to log file
   */
  file(...args: any[]): void {
      LOGSTREAM.write(args.join(' ') + '\n');
  }

  /**
   * Clear the log file (call at start of new user request)
   */
  clearFile(): void {
    LOGSTREAM.end();
    LOGSTREAM = fs.createWriteStream(LOG_FILE_PATH, { flags: 'w' }); // 'w' flag truncates the file
    LOGSTREAM.write(`\n${'='.repeat(80)}\n`);
    LOGSTREAM.write(`NEW REQUEST - ${new Date().toISOString()}\n`);
    LOGSTREAM.write(`${'='.repeat(80)}\n\n`);
  }

  /**
   * Log LLM method prompts with clear labeling
   */
  logLLMPrompt(methodName: string, promptType: 'system' | 'user', content: string | object | any[]): void {
    const header = `\n${'#'.repeat(80)}\n[LLM METHOD: ${methodName}] - ${promptType.toUpperCase()} PROMPT\n${'#'.repeat(80)}\n`;
    LOGSTREAM.write(header);

    // Handle different content types
    let contentStr: string;
    if (typeof content === 'string') {
      contentStr = content;
    } else if (Array.isArray(content)) {
      // Array of message blocks - extract text content
      contentStr = content.map((item: any) => {
        if (typeof item === 'string') return item;
        if (item.text) return item.text;
        if (item.content) return typeof item.content === 'string' ? item.content : JSON.stringify(item.content, null, 2);
        return JSON.stringify(item, null, 2);
      }).join('\n\n');
    } else if (typeof content === 'object') {
      contentStr = JSON.stringify(content, null, 2);
    } else {
      contentStr = String(content);
    }

    LOGSTREAM.write(contentStr);
    LOGSTREAM.write(`\n${'#'.repeat(80)}\n\n`);
  }
}

// Export singleton instance
export const logger = new Logger();
